import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import leaf from '../../assets/images/leaf.svg';
import Pagination from '../../components/Pagination';
import { formatDate } from '../../utils/formatDate';
import Button from '../../components/Button';
import {
  QuestionData,
  QuestionsListData,
} from '../../api/service/QnaInterfaces';
import AdminQnaService from '../../api/service/admin/AdminQnaService';
import { useNavigate } from 'react-router';

const NoticeTabWrapper = styled.div``;

const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
  img {
    width: 20px;
  }
`;

const TableContainer = styled.div`
  min-height: 481px;
  overflow-x: auto;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};
`;

const TableBox = styled.div`
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: white;

  tr {
    border-bottom: 1px solid #ddd;
  }

  tr:nth-child(12) {
    border-bottom: none; /* 마지막 행의 border 제거 */
  }

  th,
  td {
    font-size: 16px;
    padding: 10px;
    text-align: center;
    vertical-align: middle; /* 세로 정렬 */
    white-space: nowrap; /* 줄바꿈 방지 */
  }

  th {
    background-color: #85c1a9;
    color: white;
    font-weight: 500;
  }

  th:first-child {
    border-top-left-radius: 10px;
  }

  th:last-child {
    border-top-right-radius: 10px;
  }

  /* 제목 열을 가장 넓게 설정 */
  th:nth-child(2),
  td:nth-child(2) {
    width: 50%; /* 제목 열의 폭을 설정 */
  }

  @media (max-width: 768px) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;

    tr {
      border-bottom: 1px solid #ddd;
    }

    th,
    td {
      font-size: 12px;
      padding: 10px;
      text-align: center;
      vertical-align: middle; /* 세로 정렬 */
      white-space: nowrap; /* 줄바꿈 방지 */
    }

    th {
      font-weight: 400;
    }

    th:first-child {
      border-top-left-radius: 0px;
    }

    th:last-child {
      border-top-right-radius: 0px;
    }

    /* 제목 열 폭 조정 */
    th:nth-child(2),
    td:nth-child(2) {
      width: 40%; /* 모바일 환경에서의 폭 */
    }
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  resize: none;
`;

const TableContent = styled.div`
  white-space: pre-wrap; /* 줄바꿈과 공백 유지 */
  word-wrap: break-word; /* 단어 단위로 줄바꿈 */
  word-break: break-word; /* 긴 단어도 강제 줄바꿈 */
  width: 100%;
  padding: 10px;
  background-color: #ebf0ee;
  min-height: 280px;
  border-bottom: 2px dashed #fff;
`;

const TableAnswer = styled.div`
  padding: 10px;
  background-color: #ebf0ee;
  min-height: 200px;
  p {
    padding-bottom: 10px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;
const ButtonContainer2 = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: center;
  gap: 10px;
`;

function AdminTab3() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState<QuestionsListData[]>([]);
  const [selectedQuestions, setSelectedQuestions] =
    useState<QuestionData | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0); //
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [answerContent, setAnswerContent] = useState<string>(''); // 답변 내용 상태

  const handleRowClick = async (index: number, questionId: string) => {
    try {
      const response = await AdminQnaService.getQuestionDetail(questionId);
      setSelectedQuestions(response.data);
      setSelectedIndex(index);
      if (response.data.answers && response.data.answers.length > 0) {
        setAnswerContent(response.data.answers[0].content);
      } else {
        setAnswerContent(''); // 답변이 없을 경우 빈 문자열 등으로 초기화
      }

      // URL 업데이트 (페이지 이동 없이)
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('questionId', questionId);
      window.history.pushState({}, '', currentUrl);
    } catch (error) {
      console.error('문의리스트:', error);
    }
  };

  const handleButtonClick = async () => {
    setSelectedQuestions(null); // 선택된 공지사항 데이터 저장

    // URL 초기화 (페이지 이동 없이)
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('questionId');
    window.history.pushState({}, '', currentUrl);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchQna(page);
  };

  useEffect(() => {
    fetchQna(0);

    // URL에서 questionId 가져오기
    const urlParams = new URLSearchParams(window.location.search);
    const questionId = urlParams.get('questionId');

    if (questionId) {
      handleRowClick(0, questionId);
    }
  }, [navigate]); // navigate를 useEffect의 의존성 배열에 추가

  const fetchQna = async (currentPage: number) => {
    try {
      const response = await AdminQnaService.getQuestionsList(currentPage, 12);
      setQuestion(response.data.content);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error(err);
      alert(err);
    }
  };

  const handleAnswerSubmit = async () => {
    if (!selectedQuestions || !answerContent) {
      alert('답변 내용을 입력해주세요.');
      return;
    }

    const data = {
      content: answerContent,
    };

    try {
      if (selectedQuestions.answers && selectedQuestions.answers.length > 0) {
        // 이미 답변이 등록된 경우 update 호출
        const answerId = selectedQuestions.answers[0].answerId; // 기존 답변 ID 가져오기
        await AdminQnaService.updateAnswersQuestion(answerId, data);
        alert('답변이 성공적으로 수정되었습니다.');
      } else {
        // 답변이 없는 경우 create 호출
        await AdminQnaService.createAnswersQuestion({
          questionId: selectedQuestions.questionId,
          ...data,
        });
        alert('답변이 성공적으로 등록되었습니다.');
      }

      fetchQna(currentPage); // 최신 질문 목록 갱신
      handleButtonClick();
    } catch (error) {
      console.error('답변 처리 실패:', error);
      alert(error);
    }
  };

  return (
    <NoticeTabWrapper>
      <NoticeHeader>
        <img src={leaf} alt="leaf" />
        <h1>문의 게시판</h1>
      </NoticeHeader>
      {selectedQuestions ? (
        <>
          <TableContainer id="table-container">
            <TableBox>
              <Table>
                <thead>
                  <tr>
                    <th>NO</th>
                    <th>제목</th>
                    <th>작성자</th>
                    <th>작성날짜</th>
                  </tr>
                </thead>
                <tbody>
                  <tr key={selectedQuestions.questionId}>
                    <td>{selectedIndex + 1}</td>
                    <td>
                      {selectedQuestions.title.length > 30
                        ? selectedQuestions.title.substring(0, 30) + '...'
                        : selectedQuestions.title}
                    </td>
                    <td>{`${selectedQuestions.memberInfo?.name}(${selectedQuestions.memberInfo?.permissions[0]})`}</td>
                    <td>{formatDate(selectedQuestions.regDate)}</td>
                  </tr>
                </tbody>
              </Table>
            </TableBox>
            <TableContent
              dangerouslySetInnerHTML={{
                __html: selectedQuestions.content.replace(/\r\n|\n/g, '<br/>'),
              }}
            />
            <TableAnswer>
              <TextArea
                value={answerContent}
                onChange={(e) => setAnswerContent(e.target.value)}
                placeholder="답변 내용을 입력하세요."
              />
              <ButtonContainer2>
                <Button save={true} onClick={handleAnswerSubmit}>
                  {selectedQuestions?.answers &&
                  selectedQuestions.answers.length > 0
                    ? '답변수정'
                    : '답변등록'}
                </Button>
              </ButtonContainer2>
            </TableAnswer>
          </TableContainer>
          <ButtonContainer>
            <Button save={true} onClick={() => handleButtonClick()}>
              목록
            </Button>
          </ButtonContainer>
        </>
      ) : (
        <>
          <TableContainer>
            <TableBox>
              <Table>
                <thead>
                  <tr>
                    <th>NO</th>
                    <th>제목</th>
                    <th>작성자</th>
                    <th>작성날짜</th>
                  </tr>
                </thead>
                <tbody>
                  {question.length === 0 && (
                    <tr>
                      <td colSpan={4}>문의 사항이 없습니다.</td>
                    </tr>
                  )}
                  {question.map((item, index) => (
                    <tr
                      key={item.questionId}
                      onClick={() => handleRowClick(index, item.questionId)}
                    >
                      <td>{index + 1}</td>
                      <td>
                        {item.title.length > 30
                          ? item.title.substring(0, 30) + '...'
                          : item.title}
                      </td>
                      <td>{`${item.memberInfo?.name}(${item.memberInfo?.permissions[0]})`}</td>
                      <td>{formatDate(item.regDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableBox>
          </TableContainer>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </NoticeTabWrapper>
  );
}

export default AdminTab3;
